(() => {
  const lockClass = 'vo-header-drawer-lock';
  const headerRevealDuration = 240;

  class VoHeader {
    constructor(root) {
      this.root = root;
      this.headerGroup = root.closest('.shopify-section-group-header-group') || null;
      this.section = root.closest('.section--vo-header.section-header') || root.parentElement;
      this.stickyContainer = this.section;
      this.announcementSection = this.headerGroup?.querySelector('.announcement-bar-section') || null;
      this.mainShell = root.querySelector('.vo-header__main-shell');
      this.drawer = root.querySelector('[data-vo-header-drawer]');
      this.drawerOpenButtons = Array.from(root.querySelectorAll('[data-vo-header-drawer-open]'));
      this.drawerCloseButtons = root.querySelectorAll('[data-vo-header-drawer-close]');
      this.searchTriggers = Array.from(root.querySelectorAll('[data-vo-search-overlay-trigger]'));
      this.disclosures = Array.from(root.querySelectorAll('[data-vo-header-disclosure]'));
      this.megaOverlay = root.querySelector('[data-vo-header-mega-overlay]');
      this.serviceBar = root.querySelector('[data-vo-header-service]');
      this.brandLinks = Array.from(root.querySelectorAll('.vo-header__brand-link'));
      this.searchOverlayLoading = false;
      this.disclosureCloseTimer = null;
      this.headerTopOffset = root.offsetTop;
      this.headerResetOffset = 0;
      this.headerBounds = { top: 0, bottom: 0 };
      this.fullHeaderHeight = root.offsetHeight;
      this.currentHeaderHeight = root.offsetHeight;
      this.currentScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      this.hasScrolledPastHeader = false;
      this.visibilityState = 'default';
      this.announcementIsHidden = false;
      this.hiddenScrollAnchor = this.currentScrollTop;
      this.revealedScrollAnchor = this.currentScrollTop;
      this.headerRevealTimer = null;
      this.revealThreshold = 12;
      this.rehideThreshold = 48;
      this.scrollTicking = false;
      this.pendingScrollTop = this.currentScrollTop;
      this.desktopMenuMedia = window.matchMedia('(min-width: 1024px)');
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      this.initDesktopMenus();
      this.initDrawer();
      this.initBrandLinks();
      this.initSearchOverlay();
      this.measureServiceBar();
      this.initStickyBehavior();
    }

    measureServiceBar() {
      const update = () => {
        const serviceHeight = this.serviceBar?.offsetHeight || 0;
        const mainHeight = this.mainShell?.offsetHeight || this.root.offsetHeight;
        const announcementHeight = this.announcementSection?.offsetHeight || 0;
        const effectiveAnnouncementHeight = this.announcementIsHidden ? 0 : announcementHeight;
        const isCompact = this.root.classList.contains('vo-header--compact');
        const serviceCurrentHeight = isCompact ? 0 : serviceHeight;

        this.headerTopOffset = this.section?.offsetTop || this.root.offsetTop;
        this.headerResetOffset = Math.max(this.headerTopOffset - announcementHeight, 0);
        this.fullHeaderHeight = mainHeight + serviceHeight;
        this.currentHeaderHeight = mainHeight + serviceCurrentHeight;
        this.headerBounds = {
          top: this.headerTopOffset,
          bottom: this.headerTopOffset + this.fullHeaderHeight,
        };
        this.root.style.setProperty('--vo-header-service-height', serviceHeight + 'px');
        document.documentElement.style.setProperty('--vo-announcement-height', announcementHeight + 'px');
        document.documentElement.style.setProperty('--announcement-height', effectiveAnnouncementHeight + 'px');
        document.documentElement.style.setProperty('--announcement-height-tablet', effectiveAnnouncementHeight + 'px');
        document.documentElement.style.setProperty('--announcement-height-desktop', effectiveAnnouncementHeight + 'px');
        document.documentElement.style.setProperty('--header-height', this.currentHeaderHeight + 'px');
      };

      this.updateMeasurements = update;
      update();
    }

    initStickyBehavior() {
      const initialScrollTop = this.currentScrollTop;
      this.currentScrollTop = 0;
      this.applyScrollState(initialScrollTop);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
    }

    onResize() {
      this.updateMeasurements?.();
      this.applyScrollState(window.pageYOffset || document.documentElement.scrollTop || 0);
    }

    onScroll() {
      this.pendingScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;

      if (this.scrollTicking) return;

      this.scrollTicking = true;
      window.requestAnimationFrame(() => {
        this.applyScrollState(this.pendingScrollTop);
        this.scrollTicking = false;
      });
    }

    applyScrollState(scrollTop) {
      const nextScrollTop = Math.max(scrollTop, 0);
      const delta = nextScrollTop - this.currentScrollTop;

      if (nextScrollTop <= this.headerResetOffset + 2) {
        this.resetStickyState(nextScrollTop);
        this.currentScrollTop = nextScrollTop;
        this.hasScrolledPastHeader = false;
        return;
      }

      if (delta === 0) {
        this.currentScrollTop = nextScrollTop;
        return;
      }

      if (nextScrollTop > this.headerBounds.bottom) {
        this.hasScrolledPastHeader = true;
      }

      this.stickyContainer?.classList.add('shopify-section-header-sticky');

      if (!this.hasScrolledPastHeader) {
        this.currentScrollTop = nextScrollTop;
        return;
      }

      this.setAnnouncementHidden(true, true);

      if (delta > 0) {
        if (this.visibilityState === 'default') {
          this.hideStickyHeader(nextScrollTop);
        } else if (this.visibilityState === 'hidden') {
          this.hiddenScrollAnchor = Math.max(this.hiddenScrollAnchor, nextScrollTop);
        } else if (nextScrollTop - this.revealedScrollAnchor >= this.rehideThreshold) {
          this.hideStickyHeader(nextScrollTop);
        }
      } else if (this.visibilityState === 'hidden') {
        const revealedDistance = this.hiddenScrollAnchor - nextScrollTop;

        if (revealedDistance >= this.revealThreshold) {
          this.revealStickyHeader(nextScrollTop);
        }
      } else if (this.visibilityState === 'revealed') {
        this.revealedScrollAnchor = Math.min(this.revealedScrollAnchor, nextScrollTop);
      }

      this.currentScrollTop = nextScrollTop;
    }

    hideStickyHeader(anchorScrollTop = this.currentScrollTop) {
      if (this.visibilityState === 'hidden') return;
      this.visibilityState = 'hidden';
      this.hiddenScrollAnchor = anchorScrollTop;
      this.clearHeaderRevealTimer();
      document.documentElement.classList.add('vo-header-is-hidden');
      this.setAnnouncementHidden(true, true);
      this.stickyContainer?.classList.remove('animate');
      this.stickyContainer?.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeDisclosures();
    }

    revealStickyHeader(anchorScrollTop = this.currentScrollTop) {
      if (this.visibilityState === 'revealed') return;
      this.visibilityState = 'revealed';
      this.revealedScrollAnchor = anchorScrollTop;
      this.clearHeaderRevealTimer();
      this.setAnnouncementHidden(true, true);
      this.setCompactState(false);
      this.stickyContainer?.classList.add('shopify-section-header-sticky', 'animate');
      this.stickyContainer?.classList.remove('shopify-section-header-hidden');
      this.headerRevealTimer = window.setTimeout(() => {
        document.documentElement.classList.remove('vo-header-is-hidden');
        this.headerRevealTimer = null;
      }, headerRevealDuration);
    }

    resetStickyState(anchorScrollTop = this.currentScrollTop) {
      this.visibilityState = 'default';
      this.hiddenScrollAnchor = anchorScrollTop;
      this.revealedScrollAnchor = anchorScrollTop;
      this.clearHeaderRevealTimer();
      document.documentElement.classList.remove('vo-header-is-hidden');
      this.setAnnouncementHidden(false);
      this.stickyContainer?.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
      this.setCompactState(false);
    }

    clearHeaderRevealTimer() {
      if (!this.headerRevealTimer) return;
      window.clearTimeout(this.headerRevealTimer);
      this.headerRevealTimer = null;
    }

    setAnnouncementHidden(isHidden, shouldAnimate = false) {
      if (!this.announcementSection || this.announcementIsHidden === isHidden) return;

      this.announcementIsHidden = isHidden;
      this.announcementSection.classList.toggle('shopify-section-announcement-sticky', isHidden);
      this.announcementSection.classList.toggle('shopify-section-announcement-hidden', isHidden);
      this.announcementSection.classList.toggle('animate', shouldAnimate && isHidden);
      this.updateMeasurements?.();
    }

    setCompactState(isCompact) {
      if (this.root.classList.contains('vo-header--compact') === isCompact) return;
      this.root.classList.toggle('vo-header--compact', isCompact);
      this.updateMeasurements?.();
    }

    initDesktopMenus() {
      if (!this.disclosures.length) return;

      this.disclosures.forEach((disclosure) => {
        const summary = disclosure.querySelector('summary');

        disclosure.addEventListener('toggle', () => {
          this.updateMegaOverlay();

          if (!disclosure.open) return;

          this.disclosures.forEach((otherDisclosure) => {
            if (otherDisclosure !== disclosure) {
              otherDisclosure.open = false;
            }
          });
        });

        disclosure.addEventListener('mouseenter', () => {
          if (!this.isDesktopMenuHoverEnabled()) return;
          this.clearDisclosureCloseTimer();
          this.openDisclosure(disclosure);
        });

        disclosure.addEventListener('mouseleave', () => {
          if (!this.isDesktopMenuHoverEnabled()) return;
          this.scheduleDisclosureClose();
        });

        disclosure.addEventListener('focusin', () => {
          this.clearDisclosureCloseTimer();
          this.openDisclosure(disclosure);
        });

        summary?.addEventListener('click', (event) => {
          if (!this.isDesktopMenuHoverEnabled()) return;
          if (event.target.closest('a')) return;
          event.preventDefault();
          this.openDisclosure(disclosure);
        });
      });

      // Close on an actual outside target, not when a disabled control clears focus.
      ['click', 'focusin'].forEach((eventName) => {
        document.addEventListener(eventName, (event) => {
          const targetDisclosure = event.target.closest('[data-vo-header-disclosure]');

          if (!targetDisclosure || !this.root.contains(targetDisclosure)) {
            this.closeDisclosures();
          }
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeDisclosures();
        }
      });

      this.desktopMenuMedia.addEventListener('change', () => {
        this.closeDisclosures();
      });

      if (this.megaOverlay) {
        this.megaOverlay.addEventListener('click', () => {
          this.closeDisclosures();
        });
      }

      if (this.serviceBar) {
        this.serviceBar.addEventListener('mouseenter', () => {
          if (!this.isDesktopMenuHoverEnabled()) return;
          this.clearDisclosureCloseTimer();
        });

        this.serviceBar.addEventListener('mouseleave', () => {
          if (!this.isDesktopMenuHoverEnabled()) return;
          this.scheduleDisclosureClose();
        });
      }
    }

    updateMegaOverlay() {
      const anyOpen = this.disclosures.some((d) => d.open);
      this.root.classList.toggle('vo-header--mega-open', anyOpen);

      if (!this.megaOverlay) return;
      this.megaOverlay.hidden = !anyOpen;
      this.megaOverlay.classList.toggle('is-open', anyOpen);
    }

    isDesktopMenuHoverEnabled() {
      return this.desktopMenuMedia.matches;
    }

    openDisclosure(disclosure) {
      this.disclosures.forEach((otherDisclosure) => {
        otherDisclosure.open = otherDisclosure === disclosure;
      });
    }

    scheduleDisclosureClose(delay = 120) {
      this.clearDisclosureCloseTimer();
      this.disclosureCloseTimer = window.setTimeout(() => {
        this.closeDisclosures();
      }, delay);
    }

    clearDisclosureCloseTimer() {
      if (this.disclosureCloseTimer) {
        window.clearTimeout(this.disclosureCloseTimer);
        this.disclosureCloseTimer = null;
      }
    }

    closeDisclosures() {
      this.clearDisclosureCloseTimer();
      this.disclosures.forEach((disclosure) => {
        disclosure.open = false;
      });
      this.updateMegaOverlay();
    }

    initDrawer() {
      if (!this.drawer || !this.drawerOpenButtons.length) return;

      this.drawerPanel = this.drawer.querySelector('.vo-header__drawer-panel');
      this.drawerSubmenuToggles = Array.from(this.drawer.querySelectorAll('[data-vo-drawer-submenu-toggle]'));
      this.drawerSubmenuBacks = Array.from(this.drawer.querySelectorAll('[data-vo-drawer-submenu-back]'));
      this.syncDrawerCarouselHeadingLinks();

      this.drawerOpenButtons.forEach((button) => {
        button.addEventListener('click', () => this.openDrawer());
      });

      this.drawerCloseButtons.forEach((button) => {
        button.addEventListener('click', () => this.closeDrawer());
      });

      this.drawerSubmenuToggles.forEach((button) => {
        button.addEventListener('click', () => {
          const index = button.dataset.voDrawerSubmenuToggle;
          this.openDrawerSubmenu(index);
        });
      });

      this.drawerSubmenuBacks.forEach((button) => {
        button.addEventListener('click', () => {
          this.closeDrawerSubmenus();
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.drawer.hidden) {
          this.closeDrawer();
        }
      });
    }

    openDrawerSubmenu(index) {
      const submenu = this.drawer.querySelector('[data-vo-drawer-submenu="' + index + '"]');
      if (!submenu) return;

      this.closeDrawerSubmenus();
      this.syncDrawerCarouselHeadingLinks(submenu);

      if (this.drawerPanel) {
        this.drawerPanel.scrollTop = 0;
        this.drawerPanel.classList.add('has-submenu-open');
      }

      submenu.hidden = false;
      requestAnimationFrame(() => {
        submenu.classList.add('is-open');
      });

      const toggle = this.drawer.querySelector('[data-vo-drawer-submenu-toggle="' + index + '"]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    }

    normalizeDrawerHeading(value) {
      return String(value || '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
    }

    syncDrawerCarouselHeadingLinks(scope = this.drawer) {
      if (!scope) return;

      const submenus = scope.matches?.('[data-vo-drawer-submenu]')
        ? [scope]
        : Array.from(scope.querySelectorAll('[data-vo-drawer-submenu]'));

      submenus.forEach((submenu) => {
        const heading = submenu.querySelector('.vo-header__drawer-carousel .vo-megamenu-carousel__heading');
        const headingText = this.normalizeDrawerHeading(heading?.textContent);
        const items = Array.from(submenu.querySelectorAll('.vo-header__drawer-submenu-item'));

        items.forEach((item) => {
          if (item.dataset.voHiddenCarouselHeading === 'true') {
            item.hidden = false;
            delete item.dataset.voHiddenCarouselHeading;
          }
        });

        if (!headingText) return;

        items.forEach((item) => {
          const label = item.querySelector('a, span');
          const labelText = this.normalizeDrawerHeading(label?.textContent);

          if (labelText !== headingText) return;

          item.hidden = true;
          item.dataset.voHiddenCarouselHeading = 'true';
        });
      });
    }

    closeDrawerSubmenus() {
      const openSubmenus = this.drawer.querySelectorAll('.vo-header__drawer-submenu.is-open');
      openSubmenus.forEach((submenu) => {
        submenu.classList.remove('is-open');
        submenu.addEventListener('transitionend', function handler() {
          submenu.hidden = true;
          submenu.removeEventListener('transitionend', handler);
        });
      });

      if (this.drawerPanel) {
        this.drawerPanel.classList.remove('has-submenu-open');
      }

      this.drawerSubmenuToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
      });
    }

    openDrawer() {
      if (!this.drawer) return;

      this.drawer.hidden = false;
      this.drawerOpenButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'true');
      });
      document.body.classList.add(lockClass);
    }

    closeDrawer() {
      if (!this.drawer) return;

      this.closeDrawerSubmenus();
      this.drawer.hidden = true;
      this.drawerOpenButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });
      document.body.classList.remove(lockClass);
    }

    initBrandLinks() {
      if (!this.brandLinks.length) return;

      const normalizePath = (path) => String(path || '').replace(/\/+$/, '') || '/';

      this.brandLinks.forEach((link) => {
        link.addEventListener('click', (event) => {
          if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
          }

          let linkPath;
          try {
            linkPath = new URL(link.href, window.location.origin).pathname;
          } catch (error) {
            return;
          }

          if (normalizePath(linkPath) !== normalizePath(window.location.pathname)) return;

          event.preventDefault();
          this.closeDrawer();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      });
    }
    initSearchOverlay() {
      if (!this.searchTriggers.length) return;

      this.searchTriggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          if (event.defaultPrevented) return;
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

          event.preventDefault();
          this.openSearchOverlay(trigger);
        });
      });
    }

    async openSearchOverlay(trigger) {
      const activeOverlay = document.querySelector('[data-vo-main-search-root][data-vo-search-overlay="true"]');
      if (activeOverlay) {
        activeOverlay.querySelector('.vo-main-search__input')?.focus({ preventScroll: true });
        return;
      }

      if (this.searchOverlayLoading) return;
      this.searchOverlayLoading = true;
      trigger.setAttribute('aria-busy', 'true');

      try {
        this.closeDisclosures();
        if (this.drawer && !this.drawer.hidden) this.closeDrawer();

        await Promise.all([
          this.ensureStyle(trigger.dataset.voSearchOverlayCss),
          this.ensureStyle(trigger.dataset.voSearchProductCardCss),
        ]);

        const html = await this.fetchSearchOverlayHtml(trigger.dataset.voSearchOverlayUrl || trigger.href);
        const overlay = this.buildSearchOverlay(html);
        if (!overlay?.section) throw new Error('Search overlay markup not found');

        document
          .querySelectorAll('[data-vo-main-search-root][data-vo-search-overlay="true"]')
          .forEach((section) => window.voMainSearch?.closeOverlay?.(section) || section.remove());

        overlay.section.dataset.voSearchOverlay = 'true';
        overlay.section.__voSearchTrigger = trigger;
        trigger.setAttribute('aria-expanded', 'true');

        if (overlay.style) {
          overlay.style.setAttribute('data-vo-search-overlay-style', '');
          overlay.section.__voSearchOverlayStyle = overlay.style;
          document.body.appendChild(overlay.style);
        }

        document.body.appendChild(overlay.section);
        document.body.classList.add('vo-main-search-lock');

        await Promise.all([
          this.ensureScript(trigger.dataset.voSearchProductCardJs),
          this.ensureScript(trigger.dataset.voSearchMainJs),
        ]);

        this.initInjectedSearchOverlay(overlay.section);
      } catch (error) {
        window.location.href = trigger.href;
      } finally {
        this.searchOverlayLoading = false;
        trigger.removeAttribute('aria-busy');
      }
    }

    fetchSearchOverlayHtml(url) {
      return fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      }).then((response) => {
        if (!response.ok) throw new Error('Search overlay request failed');
        return response.text();
      });
    }

    buildSearchOverlay(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('[data-vo-main-search-root]');
      if (!section) return null;

      doc.querySelectorAll('[data-vo-useful-links]').forEach((usefulLinks) => {
        section.appendChild(usefulLinks);
      });

      const selector = section.id ? '#' + section.id : '';
      const style = selector
        ? Array.from(doc.querySelectorAll('style')).find((styleEl) => styleEl.textContent.includes(selector))
        : null;

      return { section, style };
    }

    ensureStyle(href) {
      if (!href) return Promise.resolve();

      const absoluteHref = new URL(href, window.location.origin).href;
      const existing = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find((link) => link.href === absoluteHref);
      if (existing) return Promise.resolve(existing);

      return new Promise((resolve) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = absoluteHref;
        link.onload = () => resolve(link);
        link.onerror = () => resolve(link);
        document.head.appendChild(link);
      });
    }

    ensureScript(src) {
      if (!src) return Promise.resolve();
      if (src.indexOf('vo-main-search.js') !== -1 && window.__voMainSearchReady) return Promise.resolve();
      if (src.indexOf('vo-product-card.js') !== -1 && window.__voProductCardReady) return Promise.resolve();

      const absoluteSrc = new URL(src, window.location.origin).href;
      const existing = Array.from(document.scripts).find((script) => script.src === absoluteSrc);
      if (existing?.dataset.voAssetLoaded === 'true') return Promise.resolve(existing);
      if (existing) {
        return new Promise((resolve) => {
          existing.addEventListener('load', () => resolve(existing), { once: true });
          existing.addEventListener('error', () => resolve(existing), { once: true });
        });
      }

      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = absoluteSrc;
        script.defer = true;
        script.onload = () => {
          script.dataset.voAssetLoaded = 'true';
          resolve(script);
        };
        script.onerror = () => resolve(script);
        document.body.appendChild(script);
      });
    }

    initInjectedSearchOverlay(section) {
      if (window.voMainSearch?.initAll) {
        window.voMainSearch.initAll(section);
        window.voMainSearch.initUIBehaviors?.();
      } else {
        section.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
      }

      window.requestAnimationFrame(() => {
        section.querySelector('.vo-main-search__input')?.focus({ preventScroll: true });
      });
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-vo-header]').forEach((root) => {
      if (root.dataset.voHeaderReady === 'true') return;
      root.dataset.voHeaderReady = 'true';
      new VoHeader(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
